import useChat from 'src/services/useChat';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { useAlert } from 'src/hooks/useAlert';
import {
  initBooleanQuery,
  initChatNow,
  setBooleanConversation,
  setChatInput,
  setChatMode,
  setConversation,
  TChatMode,
  updateLastBooleanMessageAnswer,
  updateLastMessageAnswer
} from 'src/features/chatNow';
import { useEffect } from 'react';

export const useChatLogic = () => {
  const { handleClickAlert } = useAlert();
  const dispatch = useAppDispatch();

  const {
    useSendMessage,
    useGetConversationId,
    useGetBooleanConversationId,
    useSendBooleanMessage
  } = useChat();

  const { id: projectId } = useAppSelector((state) => state.users.project);
  const [sendMessage, { loading: sendingChatNowLoading }] = useSendMessage();

  const [sendBooleanMessage, { loading: sendingBooleanLoading }] =
    useSendBooleanMessage();

  const {
    conversation,
    chatMode,
    chatInput: chatInputValue,
    booleanConversation
  } = useAppSelector((state) => state.chatNow);

  const [getConversationId, { data, loading: chatNowLoading }] =
    useGetConversationId();

  const [
    getBooleanConversationId,
    { data: booleanConversationIdData, loading: booleanLoading }
  ] = useGetBooleanConversationId();

  const isLoading = chatMode === 'chatNow' ? chatNowLoading : booleanLoading;

  const sendingLoading =
    chatMode === 'chatNow' ? sendingChatNowLoading : sendingBooleanLoading;

  const handleSendChatNowMessage = async (content: string) => {
    if (!data) return;
    dispatch(
      setConversation([
        ...conversation,
        {
          id: `${conversation.length + 1}`,
          question: content,
          answer: '',
          createdAt: new Date().toISOString()
        }
      ])
    );
    dispatch(setChatInput(''));

    sendMessage({
      variables: {
        content: content,
        conversationId: data?.chat_getConversationId.conversationId,
        projectId
      },
      onCompleted: (data) => {
        if (!data) {
          handleClickAlert({
            horizontal: 'center',
            vertical: 'top',
            message: 'Error occured while sending message',
            severity: 'error'
          });
          return;
        }

        dispatch(
          updateLastMessageAnswer({
            answer: data.chat_chatPost.answer,
            createdAt: data.chat_chatPost.createdAt
          })
        );
      },
      onError: (error) => {
        console.log(error);
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Error occured while sending message',
          severity: 'error'
        });
      }
    });
  };

  const handleSendBooleanQueryMessage = async (content: string) => {
    if (!booleanConversationIdData) return;
    dispatch(
      setBooleanConversation([
        ...booleanConversation,
        {
          id: `${booleanConversation.length + 1}`,
          question: content,
          response: '',
          createdAt: new Date().toISOString()
        }
      ])
    );
    dispatch(setChatInput(''));

    sendBooleanMessage({
      variables: {
        content: content,
        conversationId:
          booleanConversationIdData.chat_getBooleanConversationId
            .conversationId,
        projectId: projectId
      },
      onCompleted: (data) => {
        if (!data) {
          handleClickAlert({
            horizontal: 'center',
            vertical: 'top',
            message: 'Error occured while sending message',
            severity: 'error'
          });
          return;
        }
        dispatch(updateLastBooleanMessageAnswer(data.chat_chatPostBoolean));
      },
      onError: (error) => {
        console.log(error);
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Error occured while sending message',
          severity: 'error'
        });
      }
    });
  };

  const handleSendMessage =
    chatMode === 'chatNow'
      ? handleSendChatNowMessage
      : handleSendBooleanQueryMessage;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setChatInput(event.target.value));
  };

  const handleClickTopic = (topic: TChatMode) => {
    dispatch(setChatMode(topic));
    if (topic === 'chatNow') {
      dispatch(
        setConversation([
          ...conversation,
          {
            id: '1',
            question: initChatNow.question,
            answer: initChatNow.answer,
            createdAt: new Date().toISOString()
          }
        ])
      );
    } else {
      dispatch(
        setBooleanConversation([
          ...booleanConversation,
          {
            id: '1',
            question: initBooleanQuery.question,
            response: initBooleanQuery.answer,
            createdAt: new Date().toISOString(),
            include: [],
            exclude: []
          }
        ])
      );
    }
  };

  useEffect(() => {
    if (chatMode === 'chatNow') getConversationId();
    else getBooleanConversationId();
  }, [chatMode]);

  return {
    chatInputValue,
    isLoading,
    sendingLoading,
    handleSendMessage,
    handleChange,
    handleClickTopic
  };
};
